import { useMemo, useState } from "react";
import { Row, Col, Nav, Table as ReadOnlyTable } from "react-bootstrap";
import { useDates } from "dates/DatesContext";
import useSelectedMetrics from "components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "components/custom/merchandise/MultiMetricChart";
import Loading from "components/core/blocks/Loading";
import DetailedMetric from "components/core/blocks/DetailedMetric";
import { buildDSPMetric } from "./metrics";
import { useAppSelector } from "redux/hooks";
import useDSPCreativeData from "./useDSPCreativeData";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "settings", label: "Settings" },
];
const CreativeExpander = ({ row, expanderFilters }) => {
  const [tab, setTab] = useState("overview");
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { group } = useDates();
  const {
    creative_id: creativeId,
    creative_name: name,
    creative_type: type,
    creative_size: creativeSize,
  } = row._original ?? {};
  const hasImpression = row?.dsp_impressions;

  const {
    data: creativeData,
    metadata: creativeMetadata,
    isLoading: isCreativeDataLoading,
  } = useDSPCreativeData("creative_id", true, true, { creative_id: creativeId, ...expanderFilters });

  const { data: creativeTimeSeriesData, isLoading: isCreativeTimeSeriesDataLoading } = useDSPCreativeData(
    `creative_id,${group}`,
    true,
    true,
    { creative_id: creativeId },
  );

  const data = useMemo(() => {
    return {
      date: creativeTimeSeriesData?.map((row) => ({ ...row, start_date: row.date })) ?? [],
      total: creativeData?.[0] ?? {},
      metadata: creativeMetadata?.[0] ?? {},
      isLoading: isCreativeDataLoading,
    };
  }, [creativeData, creativeMetadata, creativeTimeSeriesData, isCreativeDataLoading]);

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["dsp_sales", "dsp_cost", "dsp_units"],
    "dspLineItemExpander",
  );

  const metrics = useMemo(
    () => [
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_sales", "dsp_sales_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_total_sales", "dsp_total_sales_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_impressions", "dsp_impressions_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_clicks", "dsp_clicks_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_cost", "dsp_cost_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_total_purchases", "dsp_total_purchases_percent"),
    ],
    [marketPlace, data],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const renderTabContent = (tab) => {
    switch (tab) {
      case "settings":
        return (
          <Col xs={12} lg={6}>
            <ReadOnlyTable borderless hover>
              <tbody>
                <tr>
                  <th>Name</th>
                  <td>{name}</td>
                </tr>
                <tr>
                  <th>Creative Type</th>
                  <td>{type}</td>
                </tr>
                <tr>
                  <th>Creative Size</th>
                  <td>{creativeSize}</td>
                </tr>
              </tbody>
            </ReadOnlyTable>
          </Col>
        );
      case "overview":
        return (
          <>
            {hasImpression ?
              <>
                <Col xs={12}>
                  <MultiMetricChart
                    metrics={metrics}
                    selectedMetrics={selectedMetrics}
                    data={data?.date ?? []}
                    group={group}
                    handleLegendChange={handleLegendChange}
                    datesFormatted={true}
                    showAnnotations={false}
                  />
                </Col>
                <Col xs={12}>
                  <Row className="pt-4">
                    {metrics?.map((m, i) => {
                      return (
                        <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                          <div className="">
                            <div>
                              <DetailedMetric
                                center
                                size={"small"}
                                id={m.id}
                                toolTip={m.toolTip}
                                name={m.name}
                                value={m.overrideValue ?? m.actual}
                                target={m.target}
                                formatter={m.formatter}
                                targetFormatter={m?.targetFormatter}
                                showTarget={false}
                                targetLabel={m?.targetLabel}
                                targetColor={m.target > 0 ? "#007000" : "#d2222d"}
                              />
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Col>
              </>
            : <Col xs={12} className="text-center pb-5">
                <h4>No Data Available</h4>
                <p>This section will be populated once the creative has at least 1 impression.</p>
              </Col>
            }
          </>
        );
      default:
        return null;
    }
  };

  if (isCreativeDataLoading || isCreativeTimeSeriesDataLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <div className="bg-white">
      <Row className="pt-3">
        <Col xs={12} md={6} className="pb-4 px-5">
          <h3
            style={{
              fontSize: 22,
            }}
          >
            {name}
          </h3>
          <span className="fs-standard">{type}</span>
        </Col>
      </Row>
      <Row className="px-3">
        <Col xs={12} className="fs-4 my-3 border-bottom">
          <Nav
            variant="underline"
            onSelect={(key) => {
              return setTab(key);
            }}
            activeKey={tab}
          >
            {TABS?.map((entry) => ({
              label: entry.label,
              key: entry.key,
            }))

              ?.map(({ key, label }) => {
                return (
                  <Nav.Item key={key}>
                    <Nav.Link eventKey={key} style={{ whiteSpace: "nowrap" }}>
                      {label}
                    </Nav.Link>
                  </Nav.Item>
                );
              })}
          </Nav>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="px-5 py-3">
          {renderTabContent(tab)}
        </Col>
      </Row>
    </div>
  );
};

export default CreativeExpander;
